import {Group} from '@visx/group';
import {HeatmapRect} from '@visx/heatmap';
import {ColorScale} from '@visx/heatmap/lib/types';
import {scaleBand, scaleLinear, scaleThreshold} from '@visx/scale';
import {Accessor} from '@visx/shape/lib/types';
import {Text} from '@visx/text';
import {range} from 'd3-array';
import React, {FC, useCallback, useMemo} from 'react';
import styled, {css} from 'styled-components';

import {NewPaletteColorsEnum} from '../../../../../../libs/shared/ui/src/styles/colorStyles';
import {fontSizes, fontWeights} from '../../../../../../libs/shared/ui/src/styles/fontStyles';
import {useUserTheme} from '../../../../../../libs/shared/ui/src/theme/userThemeProvider';
import {DataAccessor} from '../../commons/accessors';
import {ChartEventHandlers, ChartProps} from '../../commons/types';
import {getHeatmapColors} from '../../helpers/colors';
import {generateEventHandlers} from '../../helpers/eventHandlers';
import {useMaxInRange} from '../../helpers/ranges';
import {X_AXIS_HEIGHT, XAxis} from '../axis/xAxis';
import {YAxis, yAxisWidth} from '../axis/yAxis';

/*
 * Constants.
 */

const LOADING_COLOR = NewPaletteColorsEnum.GREY;

/*
 * Style.
 */

const StyledRectContainer = styled.g<{$isLoading?: boolean}>`
  ${(p) =>
    !p.$isLoading &&
    css`
      &:hover {
        filter: url(#shadow);
      }
    `}
`;

const StyledSvg = styled.svg`
  overflow: visible;
`;

/*
 * Props.
 */

export interface HeatMapProps<T> extends ChartProps<T>, ChartEventHandlers<T> {
  /** Accessor for y values */
  yAccessor: DataAccessor<T>;
  /** Accessor for color values */
  colorAccessor: Accessor<T, number>;

  /** Display values in cells */
  shouldDisplayValues?: boolean;
}

/*
 * Component.
 */

export const HeatMap = <T,>({
  width,
  height,
  margin = {},
  color = NewPaletteColorsEnum.ORANGE,
  xAccessor,
  yAccessor,
  colorAccessor,
  data,
  isLoading,
  shouldDisplayValues,
  onHover,
  onUnhover,
  onClick,
}: HeatMapProps<T>): ReturnType<FC> => {
  const theme = useUserTheme();
  const [xMax, yMax] = useMaxInRange(width, height, margin, true, true);

  const [dataBins, xValues, yValues, colorValues] = useMemo(() => {
    const yVals = new Set<ReturnType<typeof yAccessor>>();
    const colorVals = new Set<ReturnType<typeof colorAccessor>>();

    const dataMap = data.reduce(
      (acc, curr) => {
        const currX = xAccessor(curr);
        const currY = yAccessor(curr);
        const currColor = colorAccessor(curr);

        acc.set(currX, {
          bin: currX,
          bins: [
            ...(acc.get(currX)?.bins ?? []),
            {
              bin: currY,
              count: currColor,
              originalData: curr,
            },
          ],
        });

        yVals.add(currY);
        colorVals.add(currColor);

        return acc;
      },
      new Map<
        ReturnType<typeof xAccessor>,
        {
          bin: ReturnType<typeof xAccessor>;
          bins: Array<{
            bin: ReturnType<typeof yAccessor>;
            count: ReturnType<typeof colorAccessor>;
            originalData: T;
          }>;
        }
      >(),
    );

    return [
      Array.from(dataMap.values()),
      Array.from(dataMap.keys()),
      Array.from(yVals),
      Array.from(colorVals),
    ];
  }, [colorAccessor, data, xAccessor, yAccessor]);

  const binToOriginalData = useCallback((cell: any) => {
    const {
      bin: {originalData},
    } = cell;
    return originalData;
  }, []);

  const [xScale, xAxisScale, yScale, yAxisScale, colorScale, textColorScale] = useMemo(() => {
    const minValue = Math.min(...colorValues);
    const maxValue = Math.max(...colorValues);
    const colorSteps = 5;
    const colorDomain = range(minValue + maxValue / colorSteps, maxValue, maxValue / colorSteps);
    const mainColor = isLoading ? LOADING_COLOR : color;
    const {binColors, textColors} = getHeatmapColors(theme, mainColor, isLoading);
    return [
      scaleLinear({
        range: [0, xMax],
        domain: [0, dataBins.length],
      }),
      scaleBand({
        range: [0, xMax],
        domain: xValues,
      }),
      scaleLinear({
        range: [0, yMax], // This is intended.
        domain: [0, yValues.length],
      }),
      scaleBand({
        range: [0, yMax], // This is intended.
        domain: yValues,
      }),
      scaleThreshold().domain(colorDomain).range(binColors),
      scaleThreshold().domain(colorDomain).range(textColors),
    ];
  }, [color, colorValues, dataBins, isLoading, xMax, xValues, yMax, yValues]);

  const [binWidth, binHeight] = useMemo(
    () => [xMax / xValues.length, yMax / yValues.length],
    [xMax, xValues, yMax, yValues],
  );

  const eventHandlers = useMemo(
    () =>
      generateEventHandlers({
        onHover,
        onUnhover,
        onClick,
        isLoading,
      }),
    [isLoading, onHover, onUnhover, onClick],
  );

  return (
    <StyledSvg width={width} height={height}>
      <filter id="shadow">
        <feDropShadow dx="0" dy="0" stdDeviation="4" floodColor="black" floodOpacity="0.15" />
      </filter>
      <Group top={margin.top || 0} left={yAxisWidth + (margin.left || 0)}>
        <XAxis scale={xAxisScale} hideAxisLine hideTickValues={isLoading} />
        <Group top={X_AXIS_HEIGHT}>
          <HeatmapRect
            data={dataBins}
            xScale={(d) => xScale(d) ?? 0}
            yScale={(d) => yScale(d) ?? 0}
            colorScale={colorScale as ColorScale}
            binWidth={binWidth}
            binHeight={binHeight}
            gap={4}
          >
            {(heatmap) =>
              heatmap.map((heatmapBins) =>
                heatmapBins.map((bin) => (
                  <StyledRectContainer
                    key={`heatmap-rect-${bin.row}-${bin.column}`}
                    $isLoading={isLoading}
                    {...eventHandlers(binToOriginalData(bin))}
                  >
                    <rect width={bin.width} height={bin.height} x={bin.x} y={bin.y} rx={4} fill={bin.color} />
                    {!isLoading && shouldDisplayValues && (
                      <Text
                        x={bin.x + binWidth / 2}
                        y={bin.y + binHeight / 2}
                        verticalAnchor="middle"
                        textAnchor="middle"
                        fontSize={fontSizes.tiny}
                        fontWeight={fontWeights.bold}
                        fill={textColorScale(bin.count ?? 0)?.toString()}
                      >
                        {bin.count?.toString()}
                      </Text>
                    )}
                  </StyledRectContainer>
                )),
              )
            }
          </HeatmapRect>
          <YAxis scale={yAxisScale} hideTickValues={isLoading} numTicks={yValues.length} />
        </Group>
      </Group>
    </StyledSvg>
  );
};
